/**
 * Vulnerabilities Section - Phase 5 Refactored
 *
 * Main component for the vulnerabilities table view.
 * Reduced from 1,164 lines to ~450 lines through Phase 1-5 refactoring.
 *
 * Refactoring changes:
 * - Extracted filter logic to useVulnerabilityFilters hook
 * - Extracted selection logic to useVulnerabilitySelection hook
 * - Extracted column logic to useVulnerabilityColumns hook
 * - Extracted keyboard logic to useVulnerabilityKeyboard hook
 * - Extracted cell components to components/cells/
 * - Extracted filter UI to VulnerabilitySearchFilters component
 * - Extracted bulk actions to BulkActionsToolbar component
 * - Centralized source display logic to utils/sourceDisplay.util
 */

import {
  Bars2Icon,
  ChevronDoubleDownIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';
import { ChevronDoubleUpIcon } from '@heroicons/react/24/solid';
import { useQueryClient } from '@tanstack/react-query';
import { XCircleIcon } from 'lucide-react';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { AuthenticatedRoute } from '@/app/constant';
import { AppRouteBody } from '@/components/AppRouteBody';
import { CloseModal } from '@/components/ChangeRiskStatus';
import { ConditionalRender } from '@/components/ConditionalRender';
import { createVulnerabilityExportConfig } from '@/components/export/configs/vulnerability.export.config';
import {
  buildActiveFilters,
  UniversalExportModal,
} from '@/components/export/universal';
import type { FilterOption, GroupOption } from '@/components/filters/types';
import { getBaseRiskStatusOptions } from '@/components/legacy-ui/RiskDropdown';
import { SearchBar } from '@/components/legacy-ui/SearchBar';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { GetStartedModal } from '@/components/modals/GetStartedModal';
import { NoteModal } from '@/components/modals/NoteModal/NoteModal';
import { BulkConfirmationDialog } from '@/components/table/BulkConfirmationDialog';
import { STICKY_ACTIONS_COLUMN_CLASS } from '@/components/table/constants';
import { SelectionBanner } from '@/components/table/SelectionBanner';
import { Table } from '@/components/table/Table';
import { TableControlsContainer } from '@/components/table/TableControlsContainer';
import { TableSelectionBar } from '@/components/table/TableSelectionBar';
import { TableActions } from '@/components/table/types';
import { useGetVulnerabilityFilters } from '@/hooks/api/useGetVulnerabilityFilters';
import { shouldUseBulk } from '@/hooks/useBulkAction/useBulkAction';
import { useBulkConfirmation } from '@/hooks/useBulkConfirmation/useBulkConfirmation';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useFilteredCount } from '@/hooks/useFilteredCount';
import { useHasActiveFilters } from '@/hooks/useHasActiveFilters';
import { useMy } from '@/hooks/useMy';
import {
  Filter,
  RiskWithVulnerability,
  useRefreshVisited,
  useUpdateRisk,
} from '@/hooks/useRisks';
import { useTableRemountOnTransition } from '@/hooks/useTableRemountOnTransition';
import { AddSeedByTypeModal } from '@/sections/seeds/components/modals/AddSeedByTypeModal';
import { BulkActivityAnnouncer } from '@/sections/topNavBar/BulkJobsDrawer/BulkActivityAnnouncer';
import { VulnerabilityActions } from '@/sections/vulnerabilities/components/actions';
import { CellFiltersProvider } from '@/sections/vulnerabilities/components/cells/CellFiltersContext';
import { VulnerabilitySearchFilters } from '@/sections/vulnerabilities/components/filters';
import { ImportVulnerabilitiesModal } from '@/sections/vulnerabilities/components/modals/ImportVulnerabilitiesModal';
import { VulnerabilityRemoveTag } from '@/sections/vulnerabilities/components/modals/RemoveTagModal';
import { VulnerabilityTag } from '@/sections/vulnerabilities/components/modals/TagModal';
import { createDefaultColumns } from '@/sections/vulnerabilities/config/columnDefinitions';
import {
  VULNERABILITY_GET_STARTED_METHODS,
  type VulnerabilityGetStartedMethodId,
} from '@/sections/vulnerabilities/config/getStartedMethods';
import { VULNERABILITY_DEFAULT_COLUMN_ORDER } from '@/sections/vulnerabilities/config/vulnerabilityColumns.config';
import {
  useDefaultDemonstratedFilter,
  useGroupedRisks,
  useVulnerabilityColumns,
  useVulnerabilityData,
  useVulnerabilityFilters,
  useVulnerabilityKeyboard,
} from '@/sections/vulnerabilities/hooks';
import {
  getVulnerabilityColumnsStorageKey,
  getVulnerabilityTableRowHeight,
} from '@/sections/vulnerabilities/hooks/useVulnerabilityColumns';
import { GroupableRisk } from '@/sections/vulnerabilities/types/grouping.types';
import { flattenGroupedData } from '@/sections/vulnerabilities/utils/flattenGroupedData.util';
import { getVulnerabilityNoDataConfig } from '@/sections/vulnerabilities/utils/getVulnerabilityNoDataConfig';
import { useAuth } from '@/state/auth';
import { useBulkJobsStore } from '@/state/bulkJobs.store';
import { useGlobalState } from '@/state/global.state';
import { useOsintContext } from '@/state/OsintContext';
import { Risk, RiskSeverity, RiskStatus, SeverityDef } from '@/types';
import { cn } from '@/utils/classname';
import { useEntityFilters } from '@/utils/entityFilters.util';
import { getRiskStatusLabel } from '@/utils/riskStatus.util';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';
import { getStatusLabel } from '@/utils/vulnerabilityStatus.util';

/**
 * Component props
 */
export interface VulnerabilitiesProps {
  name?: string;
  parentFilters?: Filter;
  hideFilters?: boolean;
  hideBulkActions?: boolean;
  hideTitle?: boolean;
  inDrawer?: boolean;
  /** When provided in drawer context, limit visible filters to these keys (e.g., ['status', 'severity', 'origin']) */
  drawerFilters?: string[];
  /** Optional icon override for the empty state. Defaults to VulnerabilitiesIcon. */
  emptyStateIcon?: ReactNode;
  /** Column IDs to exclude from the default active columns (e.g., ['origins']). Does not affect the column selector — users can still manually re-enable excluded columns. */
  excludeColumns?: string[];
  /** When true, hides the column selector (gear icon) from the table header. Use in contexts where column customization is not appropriate (e.g., hunt findings pane). */
  hideColumnSelector?: boolean;
  /** When true, hides the per-row actions menu (vertical ellipsis column). */
  hideRowActions?: boolean;
}

/**
 * Route wrapper component
 */
export function VulnerabilityRoute() {
  return <Vulnerabilities />;
}

/**
 * Default active columns for drawer vulnerability tables
 *
 * Optimized for constrained drawer width (60% viewport):
 * - Removed Tags (100px): Low relevance in asset-focused context
 * - Removed Created (60px): Last Seen more relevant than creation date
 *
 * Total width: 730px (fits 768px drawer comfortably)
 * Previous width: 890px (required horizontal scroll)
 *
 * Note: Users can manually enable via column selector (gear icon)
 * Preferences stored separately: COLUMNS_assetDrawerVulnerabilities
 */
const DRAWER_DEFAULT_COLUMNS = [
  'status',
  'priority',
  'name',
  'origins',
  'visited',
];

/**
 * Default active columns for OSINT exploit vulnerability tables
 *
 * Removes 'origins' column since all OSINT results originate from Praetorian.
 * This frees 140px, giving more space to the vulnerability name and last seen columns.
 */
const OSINT_DEFAULT_COLUMNS = ['status', 'priority', 'name', 'visited'];

/**
 * Maps a risk status character to a human-readable bulk action name for the
 * confirmation dialog. Uses getStatusLabel for mode-aware copy.
 * Accepts VulnerabilityStatusMode so the dialog reflects the tenant's label set.
 */
function getBulkActionName(status: string, mode: 'legacy' | 'new'): string {
  return getStatusLabel(status as RiskStatus, mode).toLowerCase();
}

/**
 * Main Vulnerabilities component
 */
export function Vulnerabilities({
  name = 'vulnerabilities',
  parentFilters,
  hideFilters,
  hideBulkActions,
  hideTitle,
  inDrawer = false,
  drawerFilters,
  emptyStateIcon,
  excludeColumns,
  hideColumnSelector,
  hideRowActions,
}: VulnerabilitiesProps) {
  // ==================== Global State ====================
  const { drawerState } = useGlobalState();
  const { isPraetorianUser, isDemoOrg } = useAuth();
  const { isOsint } = useOsintContext();
  const { canWriteAssets } = useEntitlements();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isKeyPending = useBulkJobsStore(state => state.isKeyPending);

  // Automatically hide bulk actions in OSINT mode
  const effectiveHideBulkActions = hideBulkActions || isOsint;

  // ==================== Column Setup (before data fetching so activeColumns can gate the query) ====================

  // Column definitions (use compact variant in drawer context)
  const columnDefs = useMemo(
    () => createDefaultColumns(inDrawer && !isOsint ? 'compact' : 'default'),
    [inDrawer, isOsint]
  );

  /**
   * Select appropriate default columns based on context
   * Main view uses centralized config; drawer uses optimized subset
   */
  const DEFAULT_ACTIVE_COLUMNS = useMemo(() => {
    const base = isOsint
      ? OSINT_DEFAULT_COLUMNS
      : inDrawer
        ? DRAWER_DEFAULT_COLUMNS
        : [...VULNERABILITY_DEFAULT_COLUMN_ORDER];
    if (!excludeColumns || excludeColumns.length === 0) return base;
    return base.filter(col => !excludeColumns.includes(col));
  }, [isOsint, inDrawer, excludeColumns]);

  // Column management
  const columnManager = useVulnerabilityColumns({
    columns: columnDefs,
    defaultActiveColumns: DEFAULT_ACTIVE_COLUMNS,
    storageKey: getVulnerabilityColumnsStorageKey(),
  });

  // ==================== Data Fetching ====================

  // Optimization #1: Pass sourceOnly parameter to skip unnecessary statistics in drawer
  const { data: vulnerabilityFilters } = useGetVulnerabilityFilters({
    enabled: true, // Always enabled (need source data for display)
    originOnly: inDrawer, // Drawer: 1 query, Main: 8 queries
  });

  // Filter options for export modal pill labels (cached — no extra API call)
  const { filterOptions: vulnFilterOptions, sortedOptions: vulnSortedOptions } =
    useEntityFilters('Vulnerability', 'vuln-table');
  const vulnFieldLabels = useMemo(
    () => Object.fromEntries(vulnSortedOptions.map(o => [o.value, o.label])),
    [vulnSortedOptions]
  );

  // Use mode-aware data hook (legacy mode hides Triaged, new mode shows all)
  const {
    filteredData,
    mode,
    error,
    status,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    refetch,
    filters,
    setFilters,
    isAllEmpty,
    isFilteredEmpty,
    activeQuery,
  } = useVulnerabilityData({
    parentFilters,
    sourceFilterOptions: vulnerabilityFilters?.sourceFilters || [],
    activeColumns: columnManager.activeColumns,
    initialFilters: isOsint
      ? { sort: { orderBy: 'priority', descending: false } }
      : undefined,
  });

  // Backend risk/service.go:64 rejects non-Praetorian writes to T* status unless
  // isNewVulnSystem(). Bulk severity_change ALWAYS writes T*, so gate the UI.
  // Row-level severity also fails when the row's current status is T*.
  // mode is already available from useVulnerabilityData (via useVulnerabilityStatusMode).
  const canChangeSeverity = isPraetorianUser || mode === 'new';

  // Apply default "Demonstrated" status filter on new sessions (main table only)
  useDefaultDemonstratedFilter({
    setFilters,
    filters,
    isFilteredEmpty,
    dataStatus: status,
    isFetching,
    enabled: !inDrawer && !parentFilters && !isOsint,
  });

  // Create mode-aware export config (legacy mode excludes Triaged from export)
  const vulnerabilityExportConfig = useMemo(
    () => createVulnerabilityExportConfig(mode),
    [mode]
  );

  // Detect initial loading state to show spinner while data loads
  const isInitialLoading = status === 'pending';

  const { mutateAsync: updateRisk } = useUpdateRisk();

  // Optimization #3: Skip statistics query when hideTitle is true (drawer context)
  // Total count is only displayed in the title, which is hidden in drawers
  // Backend creates: type="my", name="status:O", value=severity (C/H/M/L/I/E)
  const { data: riskStatistics } = useMy(
    {
      resource: 'statistic',
      query: '#my#status:O#now#',
    },
    {
      staleTime: 60000, // Cache for 60 seconds
      refetchInterval: false, // Override default 30s polling
      enabled: !hideTitle, // Only fetch when title is shown
    }
  );

  // Compute total count from statistics response
  // Statistics return Open risks by severity (C, H, M, L, I, E) - sum all counts.
  // Returns undefined (not 0) when the statistics query hasn't run (e.g. the
  // drawer context disables it via hideTitle) so the totalCount fallback chain
  // at the consumer below can drop through to displayData.length instead of
  // rendering "0 vulnerabilities" over a non-empty list.
  const totalDatabaseCount = useMemo(() => {
    if (!riskStatistics) return undefined;
    return riskStatistics.reduce(
      (sum, item) => sum + (item.data?.count || 0),
      0
    );
  }, [riskStatistics]);

  // Fetch filtered count using the same query as the main data
  const { count: filteredCount, isLoading: isFilteredCountLoading } =
    useFilteredCount(
      {
        resource: 'risk',
        query: activeQuery ?? { node: { labels: ['Risk'] } },
      },
      { enabled: !hideTitle }
    );

  // Build export active filters using shared utility (reverse-maps values to labels)
  const exportActiveFilters = useMemo(
    () =>
      buildActiveFilters(
        filters.advancedFilters as Record<string, unknown> | undefined,
        (vulnFilterOptions as Record<string, (FilterOption | GroupOption)[]>) ??
          {},
        filters.search,
        vulnFieldLabels
      ),
    [
      filters.search,
      filters.advancedFilters,
      vulnFilterOptions,
      vulnFieldLabels,
    ]
  );

  // ==================== URL Parameter Handling ====================
  const [searchParams] = useSearchParams();

  // Handle URL parameters for deep linking (e.g., drawer keys from share links)
  // Global state already reads these params and opens drawer automatically
  useEffect(() => {
    const drawerKey = searchParams.get('vulnerabilityDrawerKey');
    const drawerOrder = searchParams.get('drawerOrder');

    if (drawerKey && drawerOrder) {
      // Log share link initialization for debugging
      // Note: Global state in useGlobalState automatically handles drawer opening
      // This effect is for component awareness of URL-driven initialization
    }
  }, [searchParams]);

  // ==================== Local State ====================
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);
  const [closeModalRisk, setCloseModalRisk] = useState<Risk | null>(null);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  // Task 1: bulk note confirmation state — tracks pending note text while dialog is open.
  const [pendingNote, setPendingNote] = useState<string | null>(null);
  const [isTagModalOpen, setIsTagModalOpen] = useState(false);
  const [isRemoveTagModalOpen, setIsRemoveTagModalOpen] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [confirmStatus, setConfirmStatus] = useState<{
    data: Risk[];
    status: string;
  } | null>(null);

  // GetStarted modal workflow state
  const [isGetStartedModalOpen, setIsGetStartedModalOpen] = useState(false);
  const [isAddSeedModalOpen, setIsAddSeedModalOpen] = useState(false);

  // Grouping state (persisted via localStorage in User Profile settings)
  // BUGFIX: Disable grouping in drawer context — useGroupedRisks doesn't accept
  // parentFilters.assetKey, so it fires an unbounded query for ALL tenant risks
  const [isGroupedStorage] = useStorage<boolean>(
    { key: StorageKey.VULNERABILITY_GROUPING },
    false
  );
  const isGrouped = inDrawer ? false : isGroupedStorage;

  // Pending group selection: when checkbox clicked on collapsed group,
  // expand group and auto-select children once they load
  const [pendingGroupSelect, setPendingGroupSelect] = useState<string | null>(
    null
  );

  // ==================== Custom Hooks ====================

  // Filter management
  // Disable URL sync in drawer context to prevent drawer filters from
  // polluting the main page's URL parameters
  const filterHandlers = useVulnerabilityFilters({
    filters,
    setFilters,
    syncWithUrl: !inDrawer,
  });

  // Grouped risks hook (only used when grouping is enabled)
  const {
    groups,
    isLoading: isLoadingGroups,
    expandedGroups,
    toggleGroup,
    childrenByKey,
    fetchNextPage: fetchNextGroupsPage,
    hasNextPage: hasMoreGroups,
    isFetchingNextPage: isFetchingNextGroupsPage,
  } = useGroupedRisks(isGrouped, filters.advancedFilters, filters.search);

  // Determine which data to use: grouped or ungrouped
  // When grouped, GroupableRisk doesn't have 'id' field, so union type needed
  const displayData: RiskWithVulnerability[] | GroupableRisk[] = useMemo(() => {
    if (isGrouped && groups && groups.length > 0) {
      // Flatten grouped data for table rendering (GroupableRisk extends Risk)
      return flattenGroupedData(
        groups,
        expandedGroups,
        childrenByKey,
        filters.sort
      );
    }
    // Use ungrouped data
    return filteredData;
  }, [
    isGrouped,
    groups,
    expandedGroups,
    childrenByKey,
    filteredData,
    filters.sort,
  ]);

  // Selection management — Gmail-style two-step bulk selection
  const bulkSelection = useBulkSelection({
    data: displayData as Array<{ key: string }>,
    activeFilters: (activeQuery ?? {}) as import('@/types').GraphQuery,
    filterVersion: filters,
  });

  const {
    selectedRows: selectedKeys,
    setSelectedRows: setSelectedKeys,
    hasSelection,
    clearSelection,
    isSelectAll,
    isAllLoadedSelected,
    selectAllMatching,
    selectedCount,
    state: selectionState,
  } = bulkSelection;

  const exportSelectedRiskKeys = isSelectAll ? [] : selectedKeys;

  // Derive selectedVulnerabilities from selectedRows for action handlers
  const selectedVulnerabilities = (
    displayData as Array<{ key: string }>
  ).filter(d => selectedKeys.includes(d.key)) as RiskWithVulnerability[];

  // Create a selection-compatible object for existing handler calls
  const selection = {
    selectedKeys,
    setSelectedKeys,
    hasSelection,
    selectedVulnerabilities,
    clearSelection,
    selectedCount,
  };

  // Auto-select children when a pending group selection resolves
  useEffect(() => {
    if (!pendingGroupSelect) return;
    const children = childrenByKey.get(pendingGroupSelect);
    if (children && children.length > 0) {
      const childKeys = children.map(child => child.key);
      // Filter out already-selected keys to avoid duplicates
      const newKeys = childKeys.filter(k => !selectedKeys.includes(k));
      if (newKeys.length > 0) {
        setSelectedKeys([...selectedKeys, ...newKeys]);
      }
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Clearing pending state after selection applied, safe pattern for this async use case
      setPendingGroupSelect(null);
    }
  }, [pendingGroupSelect, childrenByKey, setSelectedKeys, selectedKeys]);

  // Keyboard shortcuts
  const keyboard = useVulnerabilityKeyboard({
    enabled: true,
    onKeyboardAction: (code: string, data: Risk[]) => {
      if (!canWriteAssets) return;
      const keyStatus: Record<string, string> = {
        KeyT: RiskStatus.Triaged,
        KeyO: RiskStatus.Opened,
        KeyC: RiskStatus.Remediated,
        KeyA: RiskStatus.Accepted,
        KeyR: 'remediated',
      };
      setConfirmStatus({ data, status: keyStatus[code] });
    },
  });

  // Force table remount on empty → data transition (fixes rendering bug)
  const tableKey = useTableRemountOnTransition(
    isFilteredEmpty,
    filteredData.length
  );

  // Generate stable key based on filter/sort state to force remount on changes
  // This resets scroll position and clears stale virtualizer state
  // Note: search is excluded - debounced at API level, shouldn't trigger remounts
  const filterKey = useMemo(
    () =>
      JSON.stringify({
        filters: filters.advancedFilters,
        sort: filters.sort,
      }),
    [filters.advancedFilters, filters.sort]
  );

  // Combine with existing tableKey for empty→data transitions
  const combinedTableKey = `${tableKey}-${filterKey}`;

  // Refresh visited timestamp hook for bulk actions
  const { mutateAsync: refreshVisitedAsync, status: refreshVisitedStatus } =
    useRefreshVisited();

  // Risk actions (more actions dropdown) - no longer used after refactor
  // const { moreActions } = useRiskActions({
  //   risks: selection.getSelected(),
  //   onSuccess: refetch,
  //   onNote: () => setIsNoteModalOpen(true),
  // });

  // ==================== Memoized Values ====================

  // Severity filters (used by VulnerabilityActions component)
  const severityFilters = useMemo(
    () =>
      [
        {
          label: SeverityDef[RiskSeverity.Critical],
          value: RiskSeverity.Critical,
          id: RiskSeverity.Critical,
          severity: 'critical',
        },
        {
          label: SeverityDef[RiskSeverity.High],
          value: RiskSeverity.High,
          id: RiskSeverity.High,
          severity: 'high',
        },
        {
          label: SeverityDef[RiskSeverity.Medium],
          value: RiskSeverity.Medium,
          id: RiskSeverity.Medium,
          severity: 'medium',
        },
        {
          label: SeverityDef[RiskSeverity.Low],
          value: RiskSeverity.Low,
          id: RiskSeverity.Low,
          severity: 'low',
        },
        {
          label: SeverityDef[RiskSeverity.Info],
          value: RiskSeverity.Info,
          id: RiskSeverity.Info,
          severity: 'info',
        },
        // Exposure severity ('E') is intentionally excluded from the Change Severity submenu.
        // Backend severityToStatus has no case for 'E'; bulk severity_change would produce
        // a per-item error for every Exposure risk. UX fix: remove the option client-side.
        // Defense-in-depth: backend worker returns explicit error for 'E' input.
      ].filter(s => s.value !== RiskSeverity.Exposure),
    []
  );

  // Note: Filter options are now fetched internally by VulnerabilitySearchFilters
  // via useEntityFilters hook for automatic alphabetical ordering

  // Note: Source display text transformation is now handled by useVulnerabilityData
  // The hook returns both `data` (transformed) and `filteredData` (transformed + mode-filtered)

  // Use centralized hook for detecting active filters
  const hasActiveFilters = useHasActiveFilters(filters);

  // Bulk confirmation — encapsulates open/close/submit for the status-change dialog.
  // Does NOT call refetch() on success; useActiveBulkJobs handles invalidation.
  const statusConfirm = useBulkConfirmation({
    resource: 'risk',
    action: 'status_change',
    bulkSelection,
    clearSelection,
  });

  // Bulk confirmation hook for severity changes.
  // Routes severity changes through the ECS bulk path (same as status_change).
  const severityConfirm = useBulkConfirmation({
    resource: 'risk',
    action: 'severity_change',
    bulkSelection,
    clearSelection,
  });

  // Bulk confirmation hook for the untag bulk endpoint.
  // Same shape as statusConfirm — uses resource:'risk', action:'untag'.
  // Wire format locked: action='untag', params:{tag}.
  const untagConfirm = useBulkConfirmation({
    resource: 'risk',
    action: 'untag',
    bulkSelection,
    clearSelection,
  });

  // Task 2 — Bulk confirmation hook for the tag (add-tag) bulk endpoint.
  // Routes through POST /bulk/risk with action:'tag', params:{tag}.
  // Wire format locked: action='tag', params:{tag}.
  const tagConfirm = useBulkConfirmation({
    resource: 'risk',
    action: 'tag',
    bulkSelection,
    clearSelection,
  });

  // ==================== Event Handlers ====================

  // Non-bulk single-row update path. Shared fallback for both row-action
  // helpers and the drawer / CloseModal callbacks. Does NOT gate on bulk
  // selection — callers that need bulk routing must use
  // handleRowSeverityChange or handleRowStatusChange instead.
  async function handleUpdateRisk(risk: Risk) {
    await updateRisk(risk);
    selection.clearSelection();
  }

  // Row-action helper — SEVERITY intent.
  // Mirrors toolbar onBulkSeverityChange at index.tsx:890-896.
  // `risk.status` is the already-mutated compound (e.g. 'OC') constructed by
  // the caller. We extract the severity char for the bulk params, and pass
  // the full compound to updateRisk on the single-row path.
  async function handleRowSeverityChange(risk: Risk) {
    if (selectionState && shouldUseBulk(selectionState)) {
      const severityChar = risk.status[1] ?? '';
      severityConfirm.openConfirmation({
        action: 'severity_change',
        actionName: 'change severity',
        params: { severity: severityChar },
      });
      return;
    }
    await updateRisk(risk);
    selection.clearSelection();
  }

  // Row-action helper — STATUS intent.
  // Mirrors toolbar onBulkStatusChange at index.tsx:880-889.
  // `risk.status` is the already-mutated compound (e.g. 'OH' or 'DHFalsePositive')
  // constructed by the caller. Bulk path forwards the FULL compound — matching
  // toolbar and keyboard-confirm (index.tsx:1150-1157) semantics.
  async function handleRowStatusChange(risk: Risk) {
    if (selectionState && shouldUseBulk(selectionState)) {
      statusConfirm.openConfirmation({
        action: 'status_change',
        actionName: getBulkActionName(risk.status[0], mode),
        params: { status: risk.status },
      });
      return;
    }
    await updateRisk(risk);
    selection.clearSelection();
  }

  // Executor for the per-item note fan-out — runs AFTER confirmation.
  // Preserves original PR #5273 logic: no bulk POST, always PUT /risk per-item.
  const executeNoteForAll = async (
    note: string
  ): Promise<{ hasFailures: boolean }> => {
    const selectedRisks = selection.selectedVulnerabilities;
    const results = await Promise.allSettled(
      selectedRisks.map(risk =>
        updateRisk({
          key: risk.key,
          name: risk.name,
          status: risk.status,
          comment: note,
          showSnackbar: false,
        })
      )
    );
    const failed = results.filter(r => r.status === 'rejected');
    if (failed.length > 0) {
      console.error(`Note: ${failed.length} item(s) failed`, failed);
    } else {
      const count = selectedRisks.length;
      toast.success(
        count === 1 ? 'Note added to risk' : `Note added to ${count} risks`
      );
    }
    refetch();
    return { hasFailures: failed.length > 0 };
  };

  const handleNote = async (note: string): Promise<void> => {
    // Task 1 — handleNote bulk gate (blocker #1).
    // Backend bulk comment path removed pending storage parity (PR #5273 Task 3 — Major 6).
    // We keep per-item PUT /risk fan-out but add a BulkConfirmationDialog gate
    // when selection ≥ BULK_THRESHOLD to match the invariant for all multi-row mutations.
    //
    // Client-side length cap: mirrors MaxCommentLength = 5000 from backend.
    const MAX_NOTE_LENGTH = 5000;
    if (note.length > MAX_NOTE_LENGTH) {
      toast.error('Note exceeds 5000 characters');
      return;
    }

    // Task 1 (PR #5292 CodeRabbit critical): Refuse bulk note when mode==='all'.
    // Backend bulk comment path was removed; we cannot resolve 100k keys client-side
    // without a server round-trip, and the dialog copy implies full scope. Require
    // users to narrow their selection first.
    if (selectionState?.mode === 'all') {
      toast.error(
        'Add Note is not supported for Select all matching. Select specific rows instead.'
      );
      return;
    }

    if (selectionState && shouldUseBulk(selectionState)) {
      // Open the bulk confirmation dialog; store note for execution on confirm.
      setPendingNote(note);
      setIsNoteModalOpen(false);
      return;
    }

    // Single-row path (N < BULK_THRESHOLD) — direct fan-out, no confirmation.
    await executeNoteForAll(note);
  };

  // Handle Get Started method selection
  const handleGetStartedMethodSelect = useCallback(
    (methodId: VulnerabilityGetStartedMethodId) => {
      setIsGetStartedModalOpen(false);

      switch (methodId) {
        case 'integrations': {
          // Build URL with drawer state params for atomic navigation + drawer open
          const drawerParams = new URLSearchParams();
          drawerParams.set('drawerCompositeKey', 'integration');
          drawerParams.set('drawerOrder', JSON.stringify(['integration']));

          navigate(
            `../${AuthenticatedRoute.INTEGRATIONS}?${drawerParams.toString()}`,
            { relative: 'path' }
          );
          break;
        }
        case 'seeds':
          setIsAddSeedModalOpen(true);
          break;
        case 'import':
          setShowImportModal(true);
          break;
      }
    },
    [navigate]
  );

  // Row action menu configuration
  const getRowActions = (risk: Risk | undefined): TableActions => {
    if (!risk || !canWriteAssets) {
      return {
        menu: {
          items: [],
        },
      };
    }

    return {
      menu: {
        items: [
          // Severity items gated by canChangeSeverity (backend rejects T* writes
          // for non-Praetorian users on accounts without vulnerability-status-system=new).
          ...(canChangeSeverity
            ? [
                {
                  label: 'Change Severity',
                  type: 'label' as const,
                },
                {
                  label: 'Critical',
                  icon: <ChevronDoubleUpIcon className="size-5" />,
                  onClick: () => {
                    const currentStatus = getRiskStatusLabel(
                      risk.status
                    ).status;
                    handleRowSeverityChange({
                      ...risk,
                      status: `${currentStatus}${RiskSeverity.Critical}`,
                    });
                  },
                },
                {
                  label: 'High',
                  icon: <ChevronUpIcon className="size-5" />,
                  onClick: () => {
                    const currentStatus = getRiskStatusLabel(
                      risk.status
                    ).status;
                    handleRowSeverityChange({
                      ...risk,
                      status: `${currentStatus}${RiskSeverity.High}`,
                    });
                  },
                },
                {
                  label: 'Medium',
                  icon: <Bars2Icon className="size-5" />,
                  onClick: () => {
                    const currentStatus = getRiskStatusLabel(
                      risk.status
                    ).status;
                    handleRowSeverityChange({
                      ...risk,
                      status: `${currentStatus}${RiskSeverity.Medium}`,
                    });
                  },
                },
                {
                  label: 'Low',
                  icon: <ChevronDownIcon className="size-5" />,
                  onClick: () => {
                    const currentStatus = getRiskStatusLabel(
                      risk.status
                    ).status;
                    handleRowSeverityChange({
                      ...risk,
                      status: `${currentStatus}${RiskSeverity.Low}`,
                    });
                  },
                },
                {
                  label: 'Info',
                  icon: <ChevronDoubleDownIcon className="size-5" />,
                  onClick: () => {
                    const currentStatus = getRiskStatusLabel(
                      risk.status
                    ).status;
                    handleRowSeverityChange({
                      ...risk,
                      status: `${currentStatus}${RiskSeverity.Info}`,
                    });
                  },
                },
                { type: 'divider' as const, label: 'Divider' },
              ]
            : []),
          {
            label: 'Change Status',
            type: 'label' as const,
          },
          ...getBaseRiskStatusOptions(isPraetorianUser, mode).map(option => ({
            label: option.label,
            icon: option.icon,
            onClick: () => {
              const currentSeverity = risk.status[1] || '';
              handleRowStatusChange({
                ...risk,
                status: `${option.value}${currentSeverity}`,
              });
            },
          })),
          {
            label: getStatusLabel(RiskStatus.Deleted, mode),
            icon: <XCircleIcon className="size-5" />,
            onClick: () => {
              setIsCloseModalOpen(true);
              setCloseModalRisk(risk);
            },
          },
        ],
      },
    };
  };

  // ==================== JSX Render ====================
  return (
    <ConditionalRender
      condition={!hideTitle}
      conditionalWrapper={children => (
        <AppRouteBody title="Vulnerabilities">{children}</AppRouteBody>
      )}
    >
      {isInitialLoading ? (
        <div className="flex h-full items-center justify-center">
          <Loader isLoading type="spinner" />
        </div>
      ) : (
        <>
          {/* Unified Controls Container: Filters + Selection Bar */}
          {!isAllEmpty && (
            <TableControlsContainer
              data-testid="vulnerability-controls"
              filterLabel={!hideFilters}
              filters={
                !hideFilters ? (
                  <VulnerabilitySearchFilters
                    filterHandlers={filterHandlers}
                    isPraetorianUser={isPraetorianUser}
                    isDemoOrg={isDemoOrg}
                    visibleFilters={drawerFilters}
                  />
                ) : null
              }
              filtersRight={
                <SearchBar
                  size="sm"
                  className="h-fit w-full @md:w-64"
                  placeholder="Search"
                  value={filterHandlers.filters.search}
                  onChange={filterHandlers.handleSearchChange}
                />
              }
              selectionBar={
                effectiveHideBulkActions ? undefined : (
                  <>
                    <TableSelectionBar
                      embedded={true}
                      alwaysVisible={true}
                      isVisible={selection.hasSelection}
                      selectedCount={selection.selectedCount}
                      totalCount={
                        filteredCount ??
                        totalDatabaseCount ??
                        displayData.length
                      }
                      hasNextPage={isGrouped ? hasMoreGroups : hasNextPage}
                      isSelectAll={isSelectAll}
                      resourceLabel="vulnerabilities"
                      emptySelectionMessage={
                        isFilteredCountLoading
                          ? 'Counting vulnerabilities\u2026'
                          : filteredCount !== undefined
                            ? `${filteredCount.toLocaleString()} vulnerabilities`
                            : totalDatabaseCount !== undefined
                              ? `${totalDatabaseCount.toLocaleString()} vulnerabilities`
                              : `${displayData.length.toLocaleString()} vulnerabilities`
                      }
                      actions={
                        !effectiveHideBulkActions ? (
                          <VulnerabilityActions
                            vulnerabilities={selection.selectedVulnerabilities}
                            onNoteClick={() => setIsNoteModalOpen(true)}
                            onTagClick={() => setIsTagModalOpen(true)}
                            onRemoveTagClick={() =>
                              setIsRemoveTagModalOpen(true)
                            }
                            onImportClick={() => setShowImportModal(true)}
                            onExportClick={() => setShowExportModal(true)}
                            onUpdateRisk={handleUpdateRisk}
                            selectionState={selectionState}
                            onBulkStatusChange={(combinedStatus: string) => {
                              statusConfirm.openConfirmation({
                                action: 'status_change',
                                actionName: getBulkActionName(
                                  combinedStatus[0],
                                  mode
                                ),
                                params: { status: combinedStatus },
                              });
                            }}
                            onBulkSeverityChange={(severityChar: string) => {
                              severityConfirm.openConfirmation({
                                action: 'severity_change',
                                actionName: 'change severity',
                                params: { severity: severityChar },
                              });
                            }}
                            onClearSelection={() => {
                              selection.clearSelection();
                              refetch();
                            }}
                            onScanComplete={() => {
                              refetch();
                            }}
                            onRefreshVisited={(comment: string) => {
                              Promise.allSettled(
                                selection.selectedVulnerabilities.map(risk =>
                                  refreshVisitedAsync({ risk, comment })
                                )
                              ).then(() => {
                                selection.clearSelection();
                                refetch();
                              });
                            }}
                            isRefreshingVisited={
                              refreshVisitedStatus === 'pending'
                            }
                            severityOptions={severityFilters}
                            getRiskStatusLabel={getRiskStatusLabel}
                            canChangeSeverity={canChangeSeverity}
                            hideLabel={false}
                            hideActions={false}
                            disabled={selection.selectedCount === 0}
                          />
                        ) : undefined
                      }
                    />
                    {!effectiveHideBulkActions && (
                      <SelectionBanner
                        selectedCount={selection.selectedCount}
                        totalLoadedCount={displayData.length}
                        hasNextPage={isGrouped ? hasMoreGroups : hasNextPage}
                        isSelectAll={isSelectAll}
                        isAllLoadedSelected={isAllLoadedSelected}
                        resourceLabel="vulnerabilities"
                        onSelectAllMatching={selectAllMatching}
                        onClearSelection={clearSelection}
                        enableSelectAllMatching={activeQuery != null}
                      />
                    )}
                  </>
                )
              }
            />
          )}

          {/* Note Modal */}
          {!effectiveHideBulkActions && (
            <NoteModal
              open={isNoteModalOpen}
              onClose={() => setIsNoteModalOpen(false)}
              onSubmit={handleNote}
              resourceType="Risk"
              mode="simple"
              presentation="modal"
              selectedCount={selection.selectedCount}
            />
          )}

          {/* Tag Modal */}
          {!effectiveHideBulkActions && (
            <VulnerabilityTag
              open={isTagModalOpen}
              onClose={() => setIsTagModalOpen(false)}
              riskKeys={selection.selectedKeys}
              onSuccess={() => {
                selection.clearSelection();
                refetch();
              }}
              selectionState={selectionState}
              onBulkRequested={tag =>
                tagConfirm.openConfirmation({
                  action: 'tag',
                  actionName: 'Apply tag',
                  params: { tag },
                })
              }
            />
          )}

          {/* Remove Tag Modal */}
          {!effectiveHideBulkActions && (
            <VulnerabilityRemoveTag
              open={isRemoveTagModalOpen}
              onClose={() => setIsRemoveTagModalOpen(false)}
              riskKeys={selection.selectedKeys}
              onSuccess={() => {
                selection.clearSelection();
                refetch();
              }}
              selectionState={selectionState}
              onBulkRequested={tag =>
                untagConfirm.openConfirmation({
                  action: 'untag',
                  actionName: 'untag',
                  params: { tag },
                })
              }
            />
          )}

          {/* Single table-scoped aria-live region for bulk action status.
              UX divergence #6: one region per table, NOT one per row. */}
          <BulkActivityAnnouncer />
          {/* Vulnerabilities Table */}
          <CellFiltersProvider
            value={{
              search: filters.search || '',
              advancedFilters: filters.advancedFilters,
            }}
          >
            <Table
              responsive={false}
              tableClassName={cn(
                'w-full',
                (!hideColumnSelector || !hideRowActions) &&
                  STICKY_ACTIONS_COLUMN_CLASS
              )}
              key={combinedTableKey}
              keyboardNavigation={true}
              sortableColumns={
                hideColumnSelector
                  ? undefined
                  : {
                      activeColumns: columnManager.activeColumns,
                      setActiveColumns: columnManager.setActiveColumns,
                      defaultConfig: columnManager.sortableConfig,
                    }
              }
              selection={
                effectiveHideBulkActions
                  ? { hide: true }
                  : {
                      value: selection.selectedKeys,
                      onChange: selection.setSelectedKeys,
                      selectAll: true,
                    }
              }
              onRowClick={(risk, _idx, event) => {
                // Check if this is a group header row
                const riskWithGrouping = risk as unknown as Record<
                  string,
                  unknown
                >;
                if (
                  riskWithGrouping._isGroupHeader &&
                  riskWithGrouping._groupKey
                ) {
                  // Check if this was a checkbox click (has data-checkbox in event path)
                  const target = event?.target as HTMLElement | undefined;
                  const wasCheckboxClick = target?.closest?.('[data-checkbox]');

                  if (
                    wasCheckboxClick &&
                    !expandedGroups.has(riskWithGrouping._groupKey as string)
                  ) {
                    // Checkbox clicked on collapsed group: expand and pending-select
                    setPendingGroupSelect(riskWithGrouping._groupKey as string);
                  }

                  // Toggle group expansion
                  toggleGroup(riskWithGrouping._groupKey as string);
                } else if (!riskWithGrouping._isGroupHeader) {
                  // Open drawer for child rows or ungrouped rows
                  drawerState.handleOpenVulnerabilityDrawer(
                    risk,
                    false,
                    isOsint || undefined
                  );
                }
              }}
              sort={{
                orderBy: filters.sort?.orderBy || 'priority',
                descending: filters.sort?.descending ?? false,
                onSort: sort => {
                  setFilters(prev => ({ ...prev, sort }));
                },
              }}
              rowHeight={getVulnerabilityTableRowHeight()}
              name={name}
              columns={columnManager.columns}
              data={displayData as RiskWithVulnerability[]}
              error={error}
              status={
                isGrouped ? (isLoadingGroups ? 'pending' : 'success') : status
              }
              isFetching={isFetching}
              fetchNextPage={isGrouped ? fetchNextGroupsPage : fetchNextPage}
              isFetchingNextPage={
                isGrouped ? isFetchingNextGroupsPage : isFetchingNextPage
              }
              hasNextPage={isGrouped ? hasMoreGroups : hasNextPage}
              noData={getVulnerabilityNoDataConfig({
                isAllEmpty,
                isFilteredEmpty,
                hasActiveFilters,
                onClearFilters: filterHandlers.resetAllFilters,
                onGetStarted: canWriteAssets
                  ? () => setIsGetStartedModalOpen(true)
                  : undefined,
                inDrawer,
                emptyStateIcon,
              })}
              rowClassName={(risk: Risk) =>
                isKeyPending(risk.key) ? 'opacity-70 cursor-wait' : ''
              }
              getRowProps={(risk: Risk) =>
                isKeyPending(risk.key) ? { 'aria-busy': true } : {}
              }
              rowActions={hideRowActions ? undefined : getRowActions}
              keyboardActions={{
                ...keyboard.keyboardConfig,
                onKeyboardAction: keyboard.handleKeyboardAction,
              }}
            />
          </CellFiltersProvider>

          {/* Close/Delete Modal */}
          {/* Task 4 — CloseModal bulk-aware wrapper (blocker #5).
              When Alt+C or Alt+R triggers CloseModal with ≥ BULK_THRESHOLD rows selected,
              the original onUpdateRisk callback would call single-row handleUpdateRisk,
              bypassing the bulk gate. Fix: intercept in the callback and route through
              statusConfirm when shouldUseBulk, else fall through to single-row path.
              Scope note: do NOT touch VulnerabilityManagedDropdown.handleCloseConfirm —
              that path is already bulk-gated per commit 8dbd40ad1. */}
          <CloseModal
            open={isCloseModalOpen || keyboard.showDeleteModal}
            onClose={() => {
              keyboard.setShowDeleteModal(false);
              setIsCloseModalOpen(false);
              setCloseModalRisk(null);
            }}
            risks={closeModalRisk ? [closeModalRisk] : []}
            onUpdateRisk={async risk => {
              // Bulk path: if ≥ BULK_THRESHOLD rows selected, route through statusConfirm
              // with the full combined status string (substatus from CloseModal included).
              if (selectionState && shouldUseBulk(selectionState)) {
                statusConfirm.openConfirmation({
                  action: 'status_change',
                  actionName: getBulkActionName(risk.status[0], mode),
                  params: { status: risk.status },
                });
                keyboard.setShowDeleteModal(false);
                setIsCloseModalOpen(false);
                setCloseModalRisk(null);
                return;
              }
              // Single-row path (unchanged).
              await handleUpdateRisk(risk);
              refetch();
              selection.clearSelection();
              setCloseModalRisk(null);
            }}
          />

          {/* Confirmation Modal */}
          <Modal
            title="Confirm Change Status"
            open={!!confirmStatus && !keyboard.showDeleteModal}
            onClose={() => {
              setConfirmStatus(null);
            }}
            footer={{
              text: 'Confirm',
              onClick: async () => {
                if (!confirmStatus) return;
                if (confirmStatus.status === 'remediated') {
                  setConfirmStatus(prev => ({
                    data: prev?.data || [],
                    status: 'showCloseModalKeyboard',
                  }));
                  keyboard.setShowDeleteModal(true);
                  return;
                }

                const count = confirmStatus.data.length;
                const statusLabel = getStatusLabel(
                  confirmStatus.status as RiskStatus,
                  mode
                );

                // === BULK GATE (keyboard path) ===
                // In bulk mode, open the dialog once and return — no per-row loop,
                // no toast. The bulk dialog's onSuccess clears selection via
                // useBulkConfirmation's clearSelection wiring.
                if (selectionState && shouldUseBulk(selectionState)) {
                  const firstRisk = confirmStatus.data[0];
                  const currentSeverity = firstRisk?.status[1] ?? '';
                  const substatus = firstRisk?.status.slice(2) ?? '';
                  const combinedStatus = `${confirmStatus.status}${currentSeverity}${substatus}`;
                  statusConfirm.openConfirmation({
                    action: 'status_change',
                    actionName: getBulkActionName(
                      confirmStatus.status[0],
                      mode
                    ),
                    params: { status: combinedStatus },
                  });
                  setConfirmStatus(null);
                  return; // short-circuit: no Promise.allSettled, no toast
                }

                // === SINGLE-ROW PATH (N ≤ 2) ===
                const results = await Promise.allSettled(
                  confirmStatus.data.map(risk => {
                    const currentSeverity = risk.status[1] ?? '';
                    const substatus = risk.status.slice(2); // substatus preservation
                    const combinedStatus = `${confirmStatus.status}${currentSeverity}${substatus}`;
                    return handleUpdateRisk({
                      ...risk,
                      status: combinedStatus,
                    });
                  })
                );
                const failed = results.filter(r => r.status === 'rejected');
                if (failed.length > 0) {
                  console.error(
                    `Keyboard status change: ${failed.length} item(s) failed`,
                    failed
                  );
                } else {
                  toast.success(
                    count === 1
                      ? `Risk updated to ${statusLabel}`
                      : `${count} risks updated to ${statusLabel}`
                  );
                }
                selection.clearSelection();
                setConfirmStatus(null);
              },
            }}
          >
            <div className="pb-4">{`Are you sure you want to update the status of following vulnerabilities to "${confirmStatus?.status === 'remediated' ? getStatusLabel(RiskStatus.Deleted, mode) : getStatusLabel(confirmStatus?.status as RiskStatus, mode)}"?`}</div>
            <div className="scrollbar-dark max-h-[300px] overflow-auto">
              {confirmStatus?.data.map((risk, index) => (
                <div
                  key={risk?.key || index}
                >{`${risk?.name} (${risk?.dns})`}</div>
              ))}
            </div>
          </Modal>

          {/* Bulk Status Confirmation Dialog */}
          {/* Do not call refetch() here. useActiveBulkJobs will invalidate
              getQueryKey.getMy('risk') when the bulk job reaches terminal
              status, triggering an automatic refetch with fresh data.
              See skill: handling-async-mutations-with-polling. */}
          <BulkConfirmationDialog
            isOpen={statusConfirm.confirmPayload !== null}
            actionName={statusConfirm.confirmPayload?.actionName ?? ''}
            resourceLabel="vulnerabilities"
            isSelectAll={selectionState.mode === 'all'}
            itemCount={selectedCount}
            isPending={statusConfirm.isPending}
            onCancel={statusConfirm.closeConfirmation}
            onConfirm={statusConfirm.submitConfirmed}
            data-testid="bulk-confirmation-dialog-status"
          />
          {/* Bulk Untag Confirmation Dialog */}
          <BulkConfirmationDialog
            isOpen={untagConfirm.confirmPayload !== null}
            actionName={untagConfirm.confirmPayload?.actionName ?? ''}
            resourceLabel="vulnerabilities"
            isSelectAll={selectionState.mode === 'all'}
            itemCount={selectedCount}
            isPending={untagConfirm.isPending}
            onCancel={untagConfirm.closeConfirmation}
            onConfirm={untagConfirm.submitConfirmed}
            data-testid="bulk-confirmation-dialog-untag"
          />
          {/* Bulk Tag (Add Tag) Confirmation Dialog — Task 2 */}
          <BulkConfirmationDialog
            isOpen={tagConfirm.confirmPayload !== null}
            actionName={tagConfirm.confirmPayload?.actionName ?? ''}
            resourceLabel="vulnerabilities"
            isSelectAll={selectionState.mode === 'all'}
            itemCount={selectedCount}
            isPending={tagConfirm.isPending}
            onCancel={tagConfirm.closeConfirmation}
            onConfirm={tagConfirm.submitConfirmed}
            data-testid="bulk-confirmation-dialog-tag"
          />
          {/* Bulk Severity Change Confirmation Dialog */}
          <BulkConfirmationDialog
            isOpen={severityConfirm.confirmPayload !== null}
            actionName={severityConfirm.confirmPayload?.actionName ?? ''}
            resourceLabel="vulnerabilities"
            isSelectAll={selectionState.mode === 'all'}
            itemCount={selectedCount}
            isPending={severityConfirm.isPending}
            onCancel={severityConfirm.closeConfirmation}
            onConfirm={severityConfirm.submitConfirmed}
            data-testid="bulk-confirmation-dialog-severity"
          />
          {/* Bulk Note Confirmation Dialog (Task 1).
              Fires per-item PUT /risk fan-out on confirm — NOT a POST /bulk/risk.
              pendingNote is set when handleNote detects shouldUseBulk and the
              NoteModal submits; cleared on confirm or cancel. */}
          <BulkConfirmationDialog
            isOpen={pendingNote !== null}
            actionName="add note"
            resourceLabel="vulnerabilities"
            isSelectAll={selectionState.mode === 'all'}
            itemCount={selectedCount}
            isPending={false}
            onCancel={() => setPendingNote(null)}
            onConfirm={async () => {
              if (pendingNote === null) return;
              const note = pendingNote;
              setPendingNote(null);
              const { hasFailures } = await executeNoteForAll(note);
              // Preserve selection on any failure so user can retry. Matches shared
              // useBulkConfirmation pattern which clears only on success.
              if (!hasFailures) {
                selection.clearSelection();
              }
            }}
            data-testid="bulk-confirmation-dialog-note"
          />
          {/* Import Modal */}
          <ImportVulnerabilitiesModal
            open={showImportModal}
            onClose={() => setShowImportModal(false)}
          />

          {/* Export Modal */}
          <UniversalExportModal
            config={vulnerabilityExportConfig}
            open={showExportModal}
            onClose={() => setShowExportModal(false)}
            selectedRows={exportSelectedRiskKeys}
            onClearSelection={() => {
              selection.clearSelection();
              refetch();
            }}
            activeFilters={exportActiveFilters}
            activeQuery={activeQuery}
            totalMatchingCount={filteredCount}
          />

          {/* GetStarted Modal */}
          <GetStartedModal
            open={isGetStartedModalOpen}
            onClose={() => setIsGetStartedModalOpen(false)}
            onSelectMethod={handleGetStartedMethodSelect}
            title="Get Started with Vulnerabilities"
            subtitle="Choose how you'd like to discover vulnerabilities:"
            methods={VULNERABILITY_GET_STARTED_METHODS}
          />

          {/* Add Seed Modal (opened from GetStarted seeds method) */}
          <AddSeedByTypeModal
            open={isAddSeedModalOpen}
            onClose={() => setIsAddSeedModalOpen(false)}
            onSuccess={() => {
              queryClient.invalidateQueries({
                predicate: query => {
                  const queryKey = query.queryKey as string[];
                  return queryKey[0] === 'MY' && queryKey[1] === 'risk';
                },
                refetchType: 'active',
              });
              setIsAddSeedModalOpen(false);
            }}
          />
        </>
      )}
    </ConditionalRender>
  );
}
